<template>
  <div class="activity-container">
    <div v-if="error" class="error">
      {{ error }}
    </div>
    
    <div v-else-if="activityData" class="activity-content">
      <!-- 垂直滑动主容器 -->
      <swiper
        :modules="modules"
        :direction="'vertical'"
        :slides-per-view="1"
        :space-between="0"
        :mousewheel="true"
        class="vertical-swiper"
      >
        <swiper-slide
          v-for="item in activityData.items"
          :key="item.id"
          :class="['activity-slide', { 'item-first': item.id === 1 }]"
        >
          <!-- 如果有子项且数量大于1，显示横向滑动 -->
          <div v-if="item.children && item.children.length > 1" class="horizontal-container">
            <!-- 背景图 -->
            <img
              v-if="item.item_h5_background_url"
              :src="item.item_h5_background_url"
              class="horizontal-background"
              alt="背景"
            />
            
            <!-- 横向滑动器 -->
             <swiper
               :modules="horizontalModules"
               :slides-per-view="1"
               :space-between="0"
              :loop="false"
              :navigation="{ nextEl: `.nav-next-${item.id}`, prevEl: `.nav-prev-${item.id}` }"
               class="horizontal-swiper"
             >
              <swiper-slide
                v-for="child in item.children"
                :key="child.id"
                class="horizontal-slide"
              >
                <div class="horizontal-content-container">
                  <img
                    :src="child.item_h5_content_url"
                    class="horizontal-image-content"
                    :alt="child.item_title"
                  />
                </div>
              </swiper-slide>
            </swiper>
            
            <!-- 左右导航按钮（边缘自动置灰） -->
            <button :class="['nav-button','nav-prev', `nav-prev-${item.id}`]" aria-label="上一张" style="background-color: #bf0008;">
              <span class="chevron left"></span>
            </button>
            <button :class="['nav-button','nav-next', `nav-next-${item.id}`]" aria-label="下一张" style="background-color: #bf0008;">
              <span class="chevron right"></span>
            </button>

          <!-- 左右滑动提示 -->
          <!-- <div class="swipe-hint">
            <span class="arrow left"></span>
            <span class="hint-text">左右滑动</span>
            <span class="arrow right"></span>
          </div> -->
          </div>
          
          <!-- 普通内容展示 -->
          <div v-else class="single-content">
            <!-- 背景图 -->
            <img
              v-if="item.item_h5_background_url"
              :src="item.item_h5_background_url"
              class="fullscreen-bg"
              alt="背景"
            />
            
            <div class="content-container">
              <!-- 图片内容 -->
              <img
                v-if="item.item_content_type === 1"
                :src="item.item_h5_content_url"
                class="image-content"
                :alt="item.item_title"
              />
              
              <!-- 视频内容 -->
              <div v-else-if="item.item_content_type === 2" class="video-container">
                <div class="custom-video-wrapper">
                  <video
                    :data-video-id="item.id"
                    :src="item.item_h5_content_url"
                    class="video-content"
                    muted
                    loop
                    playsinline
                    preload="metadata"
                    poster="https://official-website-1308345661.cos.ap-shanghai.myqcloud.com/frontend/image/1021_video_cover.jpg"
                    @click="togglePlay(item.id)"
                  >
                    您的浏览器不支持视频播放
                  </video>
                  
                  <!-- 自定义播放/暂停按钮 -->
                  <button 
                    class="custom-play-button" 
                    @click="togglePlay(item.id)"
                    v-show="!videoStates[item.id]"
                  >
                    <span class="play-icon">▶</span>
                  </button>
                </div>
                
                <!-- 叠加图片（水平居中显示） -->
                <img
                  v-if="item.item_h5_overlap_url"
                  :src="item.item_h5_overlap_url"
                  class="overlap-image"
                  alt="叠加图片"
                />
              </div>
            </div>
          </div>
        </swiper-slide>
      </swiper>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Mousewheel, Navigation } from 'swiper/modules';
import { getActivityContent, type ActivityData } from '../api/activity';

// 响应式数据
const error = ref('');
const activityData = ref<ActivityData | null>(null);

// Swiper 模块
const modules = [Mousewheel];
const horizontalModules = [Navigation];

// 视频播放状态管理
const videoStates = ref<Record<number, boolean>>({});

// 控制视频播放/暂停
const togglePlay = (itemId: number) => {
  // 使用 data 属性找到对应的video元素
  const videoElement = document.querySelector(`video[data-video-id="${itemId}"]`) as HTMLVideoElement;
  if (!videoElement) {
    console.error('视频元素未找到:', itemId);
    return;
  }
  
  if (videoElement.paused) {
    videoElement.play().catch(err => {
      console.error('播放失败:', err);
    });
    videoStates.value[itemId] = true;
  } else {
    videoElement.pause();
    videoStates.value[itemId] = false;
  }
};

// 获取活动数据
const fetchActivityData = async () => {
  try {
    error.value = '';

    const response = await getActivityContent(40, 'zh');

    if (response.code === 200) {
      activityData.value = response.data;
    } else {
      error.value = response.message || '获取数据失败';
    }
  } catch (err) {
    console.error('获取活动数据失败:', err);
    error.value = '网络错误，请稍后重试';
  }
};

onMounted(() => {
  fetchActivityData();
});
</script>

<style scoped>
.activity-container {
  height: 100vh;
  width: 100vw;
  overflow: hidden;
}

.vertical-swiper {
  height: 100vh;
  width: 100vw;
}

.activity-slide {
  position: relative;
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
}

.horizontal-container {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.horizontal-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}

.horizontal-swiper {
  height: 100%;
  width: 100%;
  position: relative;
  z-index: 2;
}

.horizontal-slide {
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.horizontal-content-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.horizontal-image-content {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  z-index: 3;
}

.single-content {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.content-container {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
}

.fullscreen-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}

.image-content {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  position: relative;
  z-index: 3;
}

/* 视频容器 */
.video-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2vh;
  z-index: 3;
  width: 100%;
  max-width: 100%;
  max-height: 100%;
}

.video-content {
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 3;
  display: block;
  cursor: pointer;
}

.custom-video-wrapper {
  position: relative;
  width: min(90vw, calc(78vh * 1052 / 593));
  max-width: 100%;
  aspect-ratio: 1052 / 593;
  display: block;
  flex-shrink: 0;
  overflow: hidden;
}

/* 自定义播放按钮 */
.custom-play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: clamp(48px, 8vw, 80px);
  height: clamp(48px, 8vw, 80px);
  background: rgba(0, 0, 0, 0.6);
  border: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
}

.custom-play-button:hover {
  background: rgba(0, 0, 0, 0.8);
  border-color: #fff;
  transform: translate(-50%, -50%) scale(1.1);
}

.custom-play-button .play-icon {
  font-size: clamp(20px, 3vw, 32px);
  color: #fff;
  margin-left: 5px;
  line-height: 1;
}

/* 叠加图片 */
.overlap-image {
  max-width: min(90vw, 800px);
  max-height: 20vh;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  z-index: 3;
}

/* 左右滑动提示 */
.swipe-hint {
  position: absolute;
  bottom: 58px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.25);
  animation: hint-fade 6s ease forwards;
}

.swipe-hint .hint-text {
  letter-spacing: 1px;
}

.swipe-hint .arrow {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-top: 2px solid rgba(255, 255, 255, 0.9);
  border-right: 2px solid rgba(255, 255, 255, 0.9);
}

.swipe-hint .arrow.left {
  transform: rotate(-135deg);
  animation: arrow-left 1.4s infinite ease-in-out;
}

.swipe-hint .arrow.right {
  transform: rotate(45deg);
  animation: arrow-right 1.4s infinite ease-in-out;
}

@keyframes arrow-left {
  0% { opacity: 0.2; transform: translateX(6px) rotate(-135deg); }
  50% { opacity: 1; transform: translateX(0) rotate(-135deg); }
  100% { opacity: 0.2; transform: translateX(6px) rotate(-135deg); }
}

@keyframes arrow-right {
  0% { opacity: 0.2; transform: translateX(-6px) rotate(45deg); }
  50% { opacity: 1; transform: translateX(0) rotate(45deg); }
  100% { opacity: 0.2; transform: translateX(-6px) rotate(45deg); }
}

@keyframes hint-fade {
  0% { opacity: 0; }
  10% { opacity: 1; }
  85% { opacity: 1; }
  100% { opacity: 0; }
}

/* 箭头导航按钮 */
.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: clamp(28px, 4vw, 40px);
  height: clamp(28px, 4vw, 40px);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.35);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  cursor: pointer;
  transition: opacity 0.2s ease, background 0.2s ease;
}

.nav-button.nav-prev { left: 12px; }
.nav-button.nav-next { right: 12px; }

.nav-button:disabled,
.nav-button.swiper-button-disabled {
  opacity: 0.35;
  cursor: default;
}

.chevron {
  display: inline-block;
  width: clamp(6px, 1vw, 10px);
  height: clamp(6px, 1vw, 10px);
  border-top: 2px solid #fff;
  border-right: 2px solid #fff;
}
.chevron.left { transform: rotate(-135deg); }
.chevron.right { transform: rotate(45deg); }

/* 移动端适配 */
@media (max-width: 768px) {
  .custom-video-wrapper {
    width: 92vw;
  }

  .overlap-image {
    max-height: 18vh;
  }

  .nav-button.nav-prev { left: 8px; }
  .nav-button.nav-next { right: 8px; }
}
</style>
